import * as THREE from "three";
import {
  CircleStairsType,
  RectStairsType,
  TurnStairsType,
} from "../../../../../types/floorplannerTypes";

export type Stair3DStepDefinition = {
  id: string;
  index: number;
  shape: THREE.Shape;
  height: number;
};

export type Stair3DGeometryOptions = {
  defaultStepSize: number;
};

/** Axis-aligned plan footprint for turn stairs (centered local space). */
export type TurnStairsPlanBounds = {
  width: number;
  depth: number;
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
};

const MIN_STAIR_DIMENSION = 0.05;
const MIN_STEP_SIZE = 0.05;
const MIN_RISER_HEIGHT_METERS = 0.05;
const STAIR_FORMULA_TARGET_METERS = 0.63;
const MAX_STEP_COUNT = 128;
const MIN_FLIGHT_STEPS = 1;
const MIN_OPEN_ANGLE = THREE.MathUtils.degToRad(2);
const MAX_OPEN_ANGLE = Math.PI * 2;
const ARC_SEGMENTS_PER_STEP = 4;
/** Minimum extrude depth so Three.js never builds an empty solid. */
const MIN_GEOMETRY_DEPTH_FALLBACK = 0.001;
/** Default tread/landing slab thickness when a step shares elevation with the previous one. */
export const STAIR_TREAD_THICKNESS_METERS = 0.04;
const HEIGHT_EPSILON = 1e-8;

export type StairStepExtrusion = {
  depth: number;
  positionZ: number;
};

/**
 * Maps cumulative step elevation to a mesh extrusion for 3D rendering.
 *
 * Rising steps become short blocks from the previous tread top to this one
 * (closed riser). Flush elevations (landings) become a thin slab so the
 * viloplan reads as a distinct pad instead of a floor-to-top pillar.
 */
export function getStairStepExtrusion(
  stepHeight: number,
  previousHeight: number,
  treadThickness: number = STAIR_TREAD_THICKNESS_METERS,
): StairStepExtrusion {
  const height = Number.isFinite(stepHeight)
    ? Math.max(stepHeight, 0)
    : 0;
  const prev = Number.isFinite(previousHeight)
    ? Math.max(previousHeight, 0)
    : 0;
  const thickness = Math.max(
    Number.isFinite(treadThickness)
      ? Math.abs(treadThickness)
      : STAIR_TREAD_THICKNESS_METERS,
    MIN_GEOMETRY_DEPTH_FALLBACK,
  );

  if (height <= prev + HEIGHT_EPSILON) {
    const depth = Math.min(thickness, height > 0 ? height : thickness);
    return {
      depth,
      positionZ: Math.max(0, height - depth),
    };
  }

  return {
    depth: height - prev,
    positionZ: prev,
  };
}

/**
 * Derives how many treads fit along a rectangular stair's run length.
 * Matches {@link createRectStair3DSteps} counting.
 */
export function getRectStairStepCount(
  stair: RectStairsType,
  options: Stair3DGeometryOptions,
): number {
  const depth = sanitizeDimension(stair.rectStairsHeight);
  const treadDepth = sanitizeStepSize(
    stair.stairStepSize,
    options.defaultStepSize,
  );
  return clampStepCount(Math.ceil(depth / treadDepth));
}

/**
 * Run length (meters) for a rectangular stair with the given step count.
 * Used by the property panel so changing Steps grows/shrinks the stair
 * like Flight 1/2 steps on turn stairs.
 */
export function rectStairsHeightForStepCount(
  stepCount: number,
  treadDepth: number | undefined,
  defaultStepSize: number,
): number {
  const count = clampStepCount(Math.round(stepCount));
  const tread = sanitizeStepSize(treadDepth, defaultStepSize);
  return count * tread;
}

/**
 * Derives how many treads fit along a circular stair's outer arc.
 * Matches {@link createCircularStair3DSteps} counting.
 */
export function getCircularStairStepCount(
  stair: CircleStairsType,
  options: Stair3DGeometryOptions,
): number {
  const radius = sanitizeDimension(stair.circleStairsWidth);
  const treadDepth = sanitizeStepSize(
    stair.stairStepSize,
    options.defaultStepSize,
  );
  const openAngle = sanitizeOpenAngle(stair.openAngle);
  return clampStepCount(Math.ceil((radius * openAngle) / treadDepth));
}

/**
 * Open angle (radians) for a circular stair with the given step count.
 * Clamped to the same open-angle limits as spiral stair geometry.
 */
export function circleStairsOpenAngleForStepCount(
  stepCount: number,
  radius: number | undefined,
  treadDepth: number | undefined,
  defaultStepSize: number,
): number {
  const count = clampStepCount(Math.round(stepCount));
  const safeRadius = sanitizeDimension(radius);
  const tread = sanitizeStepSize(treadDepth, defaultStepSize);
  return sanitizeOpenAngle((count * tread) / safeRadius);
}

export function createRectStair3DSteps(
  stair: RectStairsType,
  options: Stair3DGeometryOptions,
): Stair3DStepDefinition[] {
  const width = sanitizeDimension(stair.rectStairsWidth);
  const depth = sanitizeDimension(stair.rectStairsHeight);
  const treadDepth = sanitizeStepSize(
    stair.stairStepSize,
    options.defaultStepSize,
  );
  const riserHeight = calculateStairRiserHeightFromTreadDepth(treadDepth);
  const stepCount = getRectStairStepCount(stair, options);
  const stepDepth = depth / stepCount;

  return Array.from({ length: stepCount }, (_, index) => {
    const yMax = depth / 2 - index * stepDepth;
    const yMin = yMax - stepDepth;

    return {
      id: `${stair.id}-rect-step-${index}`,
      index,
      shape: createRectangleShape(-width / 2, yMin, width / 2, yMax),
      height: riserHeight * (index + 1),
    };
  });
}

export function createCircularStair3DSteps(
  stair: CircleStairsType,
  options: Stair3DGeometryOptions,
): Stair3DStepDefinition[] {
  const radius = sanitizeDimension(stair.circleStairsWidth);
  const treadDepth = sanitizeStepSize(
    stair.stairStepSize,
    options.defaultStepSize,
  );
  const riserHeight = calculateStairRiserHeightFromTreadDepth(treadDepth);
  const openAngle = sanitizeOpenAngle(stair.openAngle);
  const stepCount = getCircularStairStepCount(stair, options);
  const angleStep = openAngle / stepCount;

  return Array.from({ length: stepCount }, (_, index) => {
    const startAngle = index * angleStep;
    const endAngle = startAngle + angleStep;

    return {
      id: `${stair.id}-circular-step-${index}`,
      index,
      shape: createSectorShape(radius, startAngle, endAngle),
      height: riserHeight * (index + 1),
    };
  });
}

/**
 * Builds stacked extruded steps for L- or U-shaped turn stairs.
 *
 * Geometry is constructed in a corner-origin space, then translated so the
 * combined AABB is centered on the origin (matching rect stairs).
 * The intermediate landing rises one riser above flight 1 so it stays
 * visually distinct from the last flight-1 tread in 3D.
 */
export function createTurnStair3DSteps(
  stair: TurnStairsType,
  options: Stair3DGeometryOptions,
): Stair3DStepDefinition[] {
  const width = sanitizeDimension(stair.stairWidth);
  const treadDepth = sanitizeStepSize(
    stair.stairStepSize,
    options.defaultStepSize,
  );
  const landingDepth = sanitizeDimension(stair.landingDepth ?? width);
  const riserHeight = calculateStairRiserHeightFromTreadDepth(treadDepth);
  const firstFlightSteps = clampFlightSteps(stair.firstFlightSteps);
  const secondFlightSteps = clampFlightSteps(stair.secondFlightSteps);
  const isRight = stair.turnDirection !== "left";
  const isU = stair.stairShape === "U";

  const steps: Stair3DStepDefinition[] = [];
  let currentElevation = 0;
  let stepIndex = 0;

  const pushRectStep = (
    id: string,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    height: number,
  ) => {
    steps.push({
      id,
      index: stepIndex,
      shape: createRectangleShape(
        Math.min(x1, x2),
        Math.min(y1, y2),
        Math.max(x1, x2),
        Math.max(y1, y2),
      ),
      height,
    });
    stepIndex += 1;
  };

  // Flight 1: straight run along +Y from the origin.
  for (let i = 0; i < firstFlightSteps; i++) {
    currentElevation += riserHeight;
    const y1 = i * treadDepth;
    const y2 = (i + 1) * treadDepth;
    pushRectStep(
      `${stair.id}-flight1-step-${i}`,
      0,
      y1,
      width,
      y2,
      currentElevation,
    );
  }

  const flight1EndY = firstFlightSteps * treadDepth;

  // Landing rises one riser above flight 1 so it reads as its own tread in 3D
  // (flush elevation made the last flight-1 tread merge into the pad).
  // L: square corner pad sized to both flight widths (shared stairWidth).
  // U: spans both parallel runs; depth is user-controlled via landingDepth.
  currentElevation += riserHeight;
  const landingPadDepth = isU ? landingDepth : width;
  const landingY1 = flight1EndY;
  const landingY2 = flight1EndY + landingPadDepth;
  if (isU) {
    const landingX1 = isRight ? 0 : -width;
    const landingX2 = isRight ? 2 * width : width;
    pushRectStep(
      `${stair.id}-landing`,
      landingX1,
      landingY1,
      landingX2,
      landingY2,
      currentElevation,
    );
  } else {
    // L: landing matches flight 1 width in X and flight 2 width in Y (no overlap into steps).
    pushRectStep(
      `${stair.id}-landing`,
      0,
      landingY1,
      width,
      landingY2,
      currentElevation,
    );
  }

  if (isU) {
    // Flight 2: 180° return along −Y in the adjacent run.
    const returnX1 = isRight ? width : -width;
    const returnX2 = isRight ? 2 * width : 0;
    for (let j = 0; j < secondFlightSteps; j++) {
      currentElevation += riserHeight;
      const y2 = flight1EndY - j * treadDepth;
      const y1 = flight1EndY - (j + 1) * treadDepth;
      pushRectStep(
        `${stair.id}-flight2-step-${j}`,
        returnX1,
        y1,
        returnX2,
        y2,
        currentElevation,
      );
    }
  } else {
    // Flight 2: 90° turn along ±X; Y span equals flight width, flush with the landing pad.
    const flight2Y1 = flight1EndY;
    const flight2Y2 = flight1EndY + width;
    for (let j = 0; j < secondFlightSteps; j++) {
      currentElevation += riserHeight;
      if (isRight) {
        const x1 = width + j * treadDepth;
        const x2 = width + (j + 1) * treadDepth;
        pushRectStep(
          `${stair.id}-flight2-step-${j}`,
          x1,
          flight2Y1,
          x2,
          flight2Y2,
          currentElevation,
        );
      } else {
        const x1 = -(j + 1) * treadDepth;
        const x2 = -j * treadDepth;
        pushRectStep(
          `${stair.id}-flight2-step-${j}`,
          x1,
          flight2Y1,
          x2,
          flight2Y2,
          currentElevation,
        );
      }
    }
  }

  return centerStairStepsOnOrigin(steps);
}

/**
 * Returns the centered plan AABB for a turn stair (width × depth in local space).
 */
export function getTurnStairsPlanBounds(
  stair: TurnStairsType,
  options: Stair3DGeometryOptions,
): TurnStairsPlanBounds {
  const steps = createTurnStair3DSteps(stair, options);
  const bounds = new THREE.Box2();
  steps.forEach((step) => {
    bounds.union(new THREE.Box2().setFromPoints(step.shape.getPoints()));
  });
  if (bounds.isEmpty()) {
    const fallback = sanitizeDimension(stair.stairWidth);
    return {
      width: fallback,
      depth: fallback,
      minX: -fallback / 2,
      maxX: fallback / 2,
      minY: -fallback / 2,
      maxY: fallback / 2,
    };
  }
  const size = bounds.getSize(new THREE.Vector2());
  return {
    width: size.x,
    depth: size.y,
    minX: bounds.min.x,
    maxX: bounds.max.x,
    minY: bounds.min.y,
    maxY: bounds.max.y,
  };
}

export function calculateStairRiserHeightFromTreadDepth(
  treadDepth: number,
): number {
  const sanitizedTreadDepth = sanitizeStepSize(treadDepth, MIN_STEP_SIZE);
  return Math.max(
    (STAIR_FORMULA_TARGET_METERS - sanitizedTreadDepth) / 2,
    MIN_RISER_HEIGHT_METERS,
  );
}

/**
 * Computes the effective "distance from floor" for a stair primitive.
 *
 * The effective distance reflects the lowest physical point of the rotated
 * stair geometry (pitch/roll), not just the pivot base elevation.
 */
export function getStairEffectiveFloorDistance(
  stair: CircleStairsType | RectStairsType | TurnStairsType,
  options: Stair3DGeometryOptions,
): number {
  const baseElevation = getStairBaseElevation(stair);
  const pitch = stair.threeD?.rotation3D?.pitch ?? 0;
  const roll = stair.threeD?.rotation3D?.roll ?? 0;
  if (Math.abs(pitch) < 1e-8 && Math.abs(roll) < 1e-8) {
    return baseElevation;
  }

  const corners = getStairLocalCorners(stair, options);
  if (corners.length === 0) return baseElevation;

  const minLocalZ = computeMinLocalZAfterRotation(corners, pitch, roll);
  return Number.isFinite(minLocalZ) ? baseElevation + minLocalZ : baseElevation;
}

/**
 * Back-computes stair base elevation from a target effective floor distance.
 *
 * This is the inverse of {@link getStairEffectiveFloorDistance}, so entering a
 * desired distance in the Property Panel maps to the correct pivot elevation.
 */
export function stairBaseElevationFromEffectiveFloor(
  stair: CircleStairsType | RectStairsType | TurnStairsType,
  targetEffectiveFloor: number,
  options: Stair3DGeometryOptions,
): number {
  const pitch = stair.threeD?.rotation3D?.pitch ?? 0;
  const roll = stair.threeD?.rotation3D?.roll ?? 0;
  if (Math.abs(pitch) < 1e-8 && Math.abs(roll) < 1e-8) {
    return targetEffectiveFloor;
  }

  const corners = getStairLocalCorners(stair, options);
  if (corners.length === 0) return targetEffectiveFloor;

  const minLocalZ = computeMinLocalZAfterRotation(corners, pitch, roll);
  if (!Number.isFinite(minLocalZ)) return targetEffectiveFloor;
  return targetEffectiveFloor - minLocalZ;
}

function createRectangleShape(
  xMin: number,
  yMin: number,
  xMax: number,
  yMax: number,
): THREE.Shape {
  const shape = new THREE.Shape();
  shape.moveTo(xMin, yMin);
  shape.lineTo(xMax, yMin);
  shape.lineTo(xMax, yMax);
  shape.lineTo(xMin, yMax);
  shape.lineTo(xMin, yMin);
  return shape;
}

/**
 * Returns local-space corner candidates for the stair mesh stack.
 *
 * Each 2D step shape point is sampled at the bottom and top of its extruded
 * block (rising riser or flush landing slab).
 */
function getStairLocalCorners(
  stair: CircleStairsType | RectStairsType | TurnStairsType,
  options: Stair3DGeometryOptions,
): THREE.Vector3[] {
  const steps = isTurnStairs(stair)
    ? createTurnStair3DSteps(stair, options)
    : isRectStairs(stair)
      ? createRectStair3DSteps(stair, options)
      : createCircularStair3DSteps(stair, options);
  const corners: THREE.Vector3[] = [];
  steps.forEach((step, index) => {
    const previousHeight = index > 0 ? steps[index - 1].height : 0;
    const { depth, positionZ } = getStairStepExtrusion(
      step.height,
      previousHeight,
    );
    const zTop = positionZ + depth;
    const shapePoints = step.shape.extractPoints(24).shape;
    shapePoints.forEach((point) => {
      corners.push(new THREE.Vector3(point.x, point.y, positionZ));
      corners.push(new THREE.Vector3(point.x, point.y, zTop));
    });
  });
  return corners;
}

/**
 * Computes the minimum local-space Z after pitch/roll rotation.
 */
function computeMinLocalZAfterRotation(
  localCorners: THREE.Vector3[],
  pitch: number,
  roll: number,
): number {
  const rotMat = new THREE.Matrix4().makeRotationFromEuler(
    new THREE.Euler(pitch, roll, 0, "XYZ"),
  );
  let minZ = Infinity;
  const tmp = new THREE.Vector3();
  localCorners.forEach((corner) => {
    tmp.copy(corner).applyMatrix4(rotMat);
    if (tmp.z < minZ) minZ = tmp.z;
  });
  return minZ;
}

function createSectorShape(
  radius: number,
  startAngle: number,
  endAngle: number,
): THREE.Shape {
  const shape = new THREE.Shape();
  const segmentCount = Math.max(
    1,
    Math.ceil((endAngle - startAngle) * ARC_SEGMENTS_PER_STEP),
  );

  shape.moveTo(0, 0);
  for (let segment = 0; segment <= segmentCount; segment++) {
    const t = segment / segmentCount;
    const angle = THREE.MathUtils.lerp(startAngle, endAngle, t);
    shape.lineTo(radius * Math.cos(angle), radius * Math.sin(angle));
  }
  shape.lineTo(0, 0);
  return shape;
}

function sanitizeDimension(value: number | undefined): number {
  return Number.isFinite(value) && typeof value === "number"
    ? Math.max(Math.abs(value), MIN_STAIR_DIMENSION)
    : MIN_STAIR_DIMENSION;
}

/**
 * Returns a finite stair base elevation value in world units.
 */
function getStairBaseElevation(
  stair: CircleStairsType | RectStairsType | TurnStairsType,
): number {
  const baseElevation = stair.threeD?.baseElevation;
  return Number.isFinite(baseElevation) && typeof baseElevation === "number"
    ? baseElevation
    : 0;
}

function isRectStairs(
  stair: CircleStairsType | RectStairsType | TurnStairsType,
): stair is RectStairsType {
  return (stair as RectStairsType).type === "rectStairs";
}

function isTurnStairs(
  stair: CircleStairsType | RectStairsType | TurnStairsType,
): stair is TurnStairsType {
  return (stair as TurnStairsType).type === "turnStairs";
}

/**
 * Translates every step shape so the combined AABB is centered on the origin.
 */
function centerStairStepsOnOrigin(
  steps: Stair3DStepDefinition[],
): Stair3DStepDefinition[] {
  if (steps.length === 0) return steps;

  const bounds = new THREE.Box2();
  steps.forEach((step) => {
    bounds.union(new THREE.Box2().setFromPoints(step.shape.getPoints()));
  });
  if (bounds.isEmpty()) return steps;

  const center = bounds.getCenter(new THREE.Vector2());
  if (Math.abs(center.x) < 1e-12 && Math.abs(center.y) < 1e-12) {
    return steps;
  }

  return steps.map((step) => {
    const points = step.shape.getPoints();
    const centered = new THREE.Shape();
    points.forEach((point, index) => {
      const x = point.x - center.x;
      const y = point.y - center.y;
      if (index === 0) {
        centered.moveTo(x, y);
      } else {
        centered.lineTo(x, y);
      }
    });
    if (points.length > 0) {
      centered.lineTo(points[0].x - center.x, points[0].y - center.y);
    }
    return {
      ...step,
      shape: centered,
    };
  });
}

function sanitizeStepSize(value: number | undefined, fallback: number): number {
  const candidate =
    Number.isFinite(value) && typeof value === "number" ? value : fallback;
  return Math.max(Math.abs(candidate), MIN_STEP_SIZE);
}

function sanitizeOpenAngle(value: number | undefined): number {
  return THREE.MathUtils.clamp(
    Number.isFinite(value) && typeof value === "number"
      ? Math.abs(value)
      : Math.PI,
    MIN_OPEN_ANGLE,
    MAX_OPEN_ANGLE,
  );
}

function clampStepCount(value: number): number {
  return THREE.MathUtils.clamp(Math.max(1, value), 1, MAX_STEP_COUNT);
}

function clampFlightSteps(value: number | undefined): number {
  const count =
    Number.isFinite(value) && typeof value === "number"
      ? Math.round(value)
      : MIN_FLIGHT_STEPS;
  return THREE.MathUtils.clamp(count, MIN_FLIGHT_STEPS, MAX_STEP_COUNT);
}
